import React, { useEffect, useState } from 'react'
import withFormContext from 'core/components/validatedForm/withFormContext'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { v4 as uuid } from 'uuid'
import { isNilOrEmpty } from 'utils/fp'
import Text from './Text'
import Tooltip from './tooltip'
import Input from './input'
import clsx from 'clsx'

interface MultiValueComponentProps {
  onChange: (value: any) => void
  value?: any
  [key: string]: any // Allow additional props to be passed through
}

interface Props {
  id: string
  label: string
  values?: string[]
  items?: any[]
  addLabel?: string
  info?: string
  placeholderText?: string
  onChange: (value: string[]) => void
  Component?: React.ComponentType<MultiValueComponentProps>
  className?: string
}

const useStyles = makeStyles<Theme, Partial<Props>>((theme) => ({
  multiValuesField: {
    maxWidth: '400px',
  },
  header: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginBottom: theme.spacing(1),
  },
  entries: {
    display: 'grid',
    gridTemplateColumns: 'auto auto',
    gridTemplateRows: '50px',
    gridGap: theme.spacing(2),
    justifyItems: 'start',
    alignItems: 'center',
  },
  label: {
    color: theme.components.input.label.color,
  },
  minus: {
    fontWeight: 900,
    color: theme.palette.blue.main,
    justifySelf: 'end',
  },
  addNewEntry: {
    display: 'grid',
    gridTemplateColumns: '24px auto',
    gridGap: theme.spacing(1),
    marginTop: theme.spacing(1),
    alignItems: 'center',
    cursor: 'pointer',
  },
  plus: {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(2),
    fontWeight: 900,
  },
  hint: {
    cursor: 'help',
    transition: 'color .2s ease',
    color: theme.components.input.label.hint,
    '& i': {
      cursor: 'help',
      marginRight: 8,
      transition: 'color .2s ease',
      color: theme.components.input.label.hint,
    },
  },
}))

const createNewEntry = () => ({ id: uuid(), value: '' })

const getInitialEntries = (values) => {
  if (isNilOrEmpty(values)) return [createNewEntry()]
  return values.map((value) => ({
    id: uuid(),
    value,
  }))
}

const removeEmptyValuesAndStripId = (entries) => {
  const values = []
  entries.forEach(({ value }) => {
    if (!values) return
    values.push(value)
  })
  return values
}

const MultiValuesField = ({
  label,
  items: values,
  addLabel,
  placeholderText,
  onChange,
  info = '',
  Component,
  className,
}: Props) => {
  const classes = useStyles({ info })
  const [entries, setEntries] = useState(getInitialEntries(values))

  useEffect(() => {
    onChange && onChange(removeEmptyValuesAndStripId(entries))
  }, [entries])

  const handleChange = (updatedEntryId) => (event) =>
    setEntries(
      entries.map((entry) =>
        entry.id === updatedEntryId ? { id: entry.id, value: event.target.value } : entry,
      ),
    )

  const handleComponentChange = (index: number) => (value: any) => {
    const updatedEntries = entries.map((entry, i) => (i === index ? { ...entry, value } : entry))
    setEntries(updatedEntries)
  }

  const addBlankEntry = () => setEntries([...entries, createNewEntry()])

  const deleteEntry = (id) => () => setEntries(entries.filter((x) => x.id !== id))

  return (
    <div className={clsx(classes.multiValuesField, className)}>
      <div className={classes.header}>
        {label && (
          <Text variant="inputLabel" className={clsx(classes.label, 'label')}>
            {label}
          </Text>
        )}
        {info && (
          <Tooltip message={info || label} className={classes.info}>
            <Text variant="inputLabel" className={classes.hint}>
              <FontAwesomeIcon>question-circle</FontAwesomeIcon>Hint
            </Text>
          </Tooltip>
        )}
      </div>
      {entries.map(({ id, value }, index) => (
        <div key={id} className={classes.entries}>
          {Component ? (
            <Component onChange={handleComponentChange(index)} value={value} />
          ) : (
            <Input placeholder={placeholderText} value={value} onChange={handleChange(id)} />
          )}
          <FontAwesomeIcon className={classes.minus} onClick={deleteEntry(id)}>
            minus-circle
          </FontAwesomeIcon>
        </div>
      ))}
      <div className={classes.addNewEntry} onClick={addBlankEntry}>
        <FontAwesomeIcon className={classes.plus} size="lg">
          plus-circle
        </FontAwesomeIcon>
        <Text variant="body2">{addLabel || 'Add'}</Text>
      </div>
    </div>
  )
}

export default withFormContext(MultiValuesField)
